import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { UserQuery, CreateUserRequest, UpdateUserRequest } from '@troia-v3/types';
import { userService } from '../services/userService';

/**
 * Hook to fetch users list with pagination and filters
 */
export const useUsers = (query?: UserQuery) => {
  return useQuery({
    queryKey: ['users', query],
    queryFn: () => userService.getUsers(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a single user by ID
 */
export const useUser = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => userService.getUserById(id),
    enabled: options?.enabled !== undefined ? options.enabled : !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to create a new user
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequest) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Hook to update a user
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Hook to delete a user
 */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Hook to activate a user
 */
export const useActivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.activateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Hook to deactivate a user
 */
export const useDeactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Hook to resend invite (verification e-mail) to a user
 */
export const useResendInvite = () => {
  return useMutation({
    mutationFn: (id: string) => userService.resendInvite(id),
    onSuccess: () => toast.success('Convite reenviado por e-mail.'),
    onError: (error: any) =>
      toast.error(error.response?.data?.message || 'Erro ao reenviar convite'),
  });
};

/**
 * Hook to trigger a password reset for a user
 */
export const useResetUserPassword = () => {
  return useMutation({
    mutationFn: (id: string) => userService.resetPassword(id),
    onSuccess: () => toast.success('E-mail de redefinição de senha enviado.'),
    onError: (error: any) =>
      toast.error(error.response?.data?.message || 'Erro ao redefinir senha'),
  });
};

/**
 * Hook to upload avatar for a specific user
 */
export const useUploadUserAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      userService.uploadUserAvatar(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Hook to fetch users for dropdown/filters
 * - No pagination (returns ALL users)
 * - No permissions required (uses /dropdown endpoint)
 * - Minimal data (id, name, email, avatar)
 * - Use this for filters across CRM, Tickets, Attendance, etc.
 *
 * `enabled` (default `true`) — condicionar a `isOpen` em modais/drawers
 * (CROSS_MODULE_PERMISSIONS) evita disparar a query com o componente fechado.
 * Não entra na query key (extraído antes de repassar pro service/queryKey).
 */
export const useUsersDropdown = (filters?: {
  status?: 'active' | 'inactive';
  /** `'team'`: só users que compartilham team com o user logado. Ver D-context-gate. */
  scope?: 'team' | 'all';
  enabled?: boolean;
}) => {
  const { enabled, ...apiFilters } = filters ?? {};
  return useQuery({
    queryKey: ['users', 'dropdown', apiFilters],
    queryFn: () => userService.getUsersForDropdown(apiFilters),
    staleTime: 10 * 60 * 1000, // 10 minutes (dropdowns change less frequently)
    enabled: enabled ?? true,
  });
};
